import { AxiosError } from "axios";
import instanceJWT from "./apiJwt";
import { csrfHeader } from "../common/helpers/auth/csrf";
import type { RetryAxiosRequestConfig } from "../models/api/RetryAxiosRequestConfig";
import type { MarkupSettingsSnapshot } from "../models/markupSettings";

/**
 * Наценки ЛК в настройках пользователя Eplex API (CAB-778).
 *
 * Модуль `settings/common` — общий JSON на пользователя: 1С Eplex держит там свои
 * настройки под ключом `1c`, ЛК — под `lk`. Проверено на стенде 29.09.2026:
 * `POST` сливает объекты вглубь и чужие ключи не трогает, `PATCH` по пути заменяет
 * объект целиком, `null` в PATCH удаляет путь, отсутствующая запись создаётся первым
 * же PATCH. Пишем всегда одним PATCH по пути `lk.markup` — так удалённое правило
 * исчезает и на сервере (слияние через POST оставляло бы его навсегда).
 *
 * Апстрим режет частоту (~3 запроса в секунду → 429): сохранения склеивает стор,
 * а 429 на этом адресе apiJwt повторяет тихо.
 */
const ENDPOINT = "eplex/settings/common";
export const MARKUP_SETTINGS_PATH = "lk.markup";

/** Ошибку сети или сервера показывает сам раздел «Наценки», глобальный тост тут лишний. */
const SILENT: RetryAxiosRequestConfig = { _silent: true };

interface SettingsEnvelope {
  data?: {
    item?: unknown;
  };
}

interface ProxyErrorBody {
  signature?: string;
  error?: { code?: number; message?: string };
}

export type RemoteMarkupSettings = { kind: "found"; raw: unknown } | { kind: "absent" };

/**
 * «Данные не найдены» (в терминах Eplex API — error_code 407) означает лишь, что
 * записи модуля у пользователя ещё нет: так отвечает и клиенту без 1С, и новому
 * аккаунту. Это не сбой, а пустые настройки.
 */
export function isNoDataError(error: unknown): boolean {
  if (!(error instanceof AxiosError) || error.response?.status !== 500) return false;

  const body = error.response.data as ProxyErrorBody | undefined;
  if (body?.signature !== "lk_eplex_api") return false;

  const message = body.error?.message ?? "";
  return message.includes("Данные не найдены") || message.includes('"error_code":407');
}

function pickMarkupNode(item: unknown): unknown {
  if (typeof item !== "object" || item === null || Array.isArray(item)) return undefined;

  const lk = (item as Record<string, unknown>).lk;
  if (typeof lk !== "object" || lk === null) return undefined;

  return (lk as Record<string, unknown>).markup;
}

export async function fetchMarkupSettings(): Promise<RemoteMarkupSettings> {
  try {
    const response = await instanceJWT.get<SettingsEnvelope>(ENDPOINT, SILENT);
    const markup = pickMarkupNode(response.data?.data?.item);

    return typeof markup === "object" && markup !== null ? { kind: "found", raw: markup } : { kind: "absent" };
  } catch (error) {
    if (isNoDataError(error)) return { kind: "absent" };
    throw error;
  }
}

export async function saveMarkupSettings(snapshot: MarkupSettingsSnapshot): Promise<void> {
  await instanceJWT.patch(ENDPOINT, { items: { [MARKUP_SETTINGS_PATH]: snapshot } }, SILENT);
}

/**
 * Последняя несохранённая правка при закрытии вкладки. Обычный запрос браузер
 * обрывает вместе со страницей, `keepalive` он доводит до конца. Через `fetch`, а не
 * axios — тот `keepalive` не умеет; заголовок CSRF ставим сами, как это делает apiJwt.
 * Результат не ждём: страницы уже нет, сообщить некому.
 */
export function sendMarkupSettingsOnUnload(snapshot: MarkupSettingsSnapshot): void {
  const baseUrl = instanceJWT.defaults.baseURL ?? "/api/";

  try {
    fetch(`${baseUrl}${ENDPOINT}`, {
      method: "PATCH",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...csrfHeader("patch") },
      body: JSON.stringify({ items: { [MARKUP_SETTINGS_PATH]: snapshot } }),
    }).catch((error: unknown) => {
      console.error("Markup settings unload save failed: ", error);
    });
  } catch (error) {
    console.error("Markup settings unload save failed: ", error);
  }
}
